import {
  Authenticator,
  type Theme,
  ThemeProvider,
  translations,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.layer.css";
import { I18n } from "aws-amplify/utils";
import { Suspense } from "react";

import { Spinner } from "@/components/atoms/Spinner/Spinner";
import { useAuthenticated } from "@/hooks/useAuthentication";

import { AfterSignIn } from "./AfterSignIn";
import { FormHeader } from "./FormHeader";

I18n.putVocabularies(translations);
I18n.setLanguage("ja");
I18n.putVocabularies({
  ja: {
    "Sign in": "ログイン",
    "Signing in": "ログイン中",
    "Enter your email": "メールアドレス",
    "Back to Sign In": "ログインに戻る",
    "Change Password": "パスワードの変更",
    "Enter your Confirmation Code": "認証コードを入力",

    // エラーメッセージ
    "User does not exist.": "ユーザーが存在しません",
    "Incorrect username or password.":
      "メールアドレスまたはパスワードが間違っています",
    "Username/client id combination not found.": "ユーザーが存在しません",
    "Your passwords must match": "パスワードが一致しません",
    "Password must have at least 8 characters":
      "パスワードは8文字以上を入力してください",
    "Invalid verification code provided, please try again.":
      "無効な認証コードが指定されました。もう一度お試しください。",
    "Temporary password has expired and must be reset by an administrator.":
      "一時パスワードの有効期限が切れています。担当者へお問い合わせください。",
    "There is already a signed in user.": "すでにログインしています。",
    "User password cannot be reset in the current state.":
      "初回ログインが完了していない為、パスワードをリセットすることができません。担当者へお問い合わせください。",
  },
});

const theme: Theme = {
  name: "Auth",
  tokens: {
    fonts: {
      default: {
        variable: {
          value: 'Roboto, "Noto Sans JP", Helvetica, Arial, sans-serif',
        },
        static: {
          value: 'Roboto, "Noto Sans JP", Helvetica, Arial, sans-serif',
        },
      },
    },
    components: {
      authenticator: {
        router: {
          borderColor: { value: "transparent" },
          boxShadow: { value: "none" },
        },
      },
      field: {
        fontSize: { value: "14px" },
        label: {
          color: { value: "#312F3C" },
        },
      },
      fieldcontrol: {
        borderColor: { value: "#CDC9D5" },
        _focus: {
          borderColor: { value: "#1F3B8F" },
          boxShadow: { value: "0px 0px 0px 2px #CDD7F4" },
        },
      },
      button: {
        lineHeight: { value: "24px" },
        _hover: {
          borderColor: { value: "#1F3B8F" },
        },
        primary: {
          backgroundColor: { value: "#1F3B8F" },
          _hover: {
            backgroundColor: { value: "#3E64D5" },
          },
          _focus: {
            backgroundColor: { value: "#3E64D5" },
          },
        },
      },
    },
    colors: {
      primary: {
        "10": "#E2E7F9",
        "80": "#1F3B8F",
        "90": "#172B69",
      },
    },
  },
};

const formFields: React.ComponentProps<typeof Authenticator>["formFields"] = {
  signIn: {
    username: {
      placeholder: "mailaddress@airdesign.ai",
      isRequired: true,
    },
    password: {
      placeholder: "************",
      isRequired: true,
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      label: "認証コード",
    },
  },
  confirmVerifyUser: {
    confirmation_code: {
      label: "認証コード",
      placeholder: "",
    },
  },
};

const components: React.ComponentProps<typeof Authenticator>["components"] = {
  SignIn: {
    Header() {
      return (
        <div className="pl-8">
          <FormHeader>ログイン</FormHeader>
        </div>
      );
    },
  },
  ForgotPassword: {
    Header() {
      return (
        <div>
          <FormHeader>認証コード発行</FormHeader>
          <p className="mt-2 text-Medium text-gray-500">
            パスワードをリセットするための認証コードがメールアドレスに送信されます。
          </p>
        </div>
      );
    },
  },
  ConfirmResetPassword: {
    Header() {
      return (
        <div>
          <FormHeader>パスワード再設定</FormHeader>
          <p className="mt-2 text-Medium text-gray-500">
            メールアドレスに送信された認証コードと新しいパスワードを送信してください。
          </p>
        </div>
      );
    },
  },
  VerifyUser: {
    Header() {
      return (
        <div>
          <FormHeader>メールアドレスの検証</FormHeader>
          <p className="mt-2 text-Medium text-gray-500">
            メールアドレスの検証のため、認証コードを送信します。
          </p>
        </div>
      );
    },
  },
  ConfirmVerifyUser: {
    Header() {
      return (
        <div>
          <FormHeader>メールアドレスの確認</FormHeader>
          <p className="mt-2 text-Medium text-gray-500">
            メールアドレスに送信された認証コードを送信してください。
          </p>
        </div>
      );
    },
  },
  ForceNewPassword: {
    Header() {
      return (
        <div>
          <FormHeader>初回パスワード設定</FormHeader>
          <p className="mt-2 text-Medium text-gray-500">
            一時的なパスワードが設定されています。
            <br />
            新しいパスワードを設定してください。
          </p>
        </div>
      );
    },
  },
};

const LoadingFallback = () => {
  return (
    <output className="grid place-content-center">
      <Spinner className="size-5 text-primary" />
      <span className="sr-only">読み込み中…</span>
    </output>
  );
};

const AmplifyAuthenticator = () => {
  const { isConfiguring, isAuthenticated } = useAuthenticated();

  if (isConfiguring) {
    return <LoadingFallback />;
  }

  return (
    <>
      {isAuthenticated ? (
        <Suspense fallback={<LoadingFallback />}>
          <AfterSignIn />
        </Suspense>
      ) : (
        <ThemeProvider theme={theme}>
          <Authenticator
            loginMechanisms={["email"]}
            formFields={formFields}
            hideSignUp={true}
            components={components}
          >
            {() => {
              return (
                <Suspense fallback={<LoadingFallback />}>
                  <AfterSignIn />
                </Suspense>
              );
            }}
          </Authenticator>
        </ThemeProvider>
      )}
    </>
  );
};

export const AuthenticatorWrapper = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <AmplifyAuthenticator />
    </Suspense>
  );
};
